<template>
    <div class="application flex flex-col" :style="bgStyle">
        <nav-bar></nav-bar>
        <side-bar></side-bar>
        <overlay />

        <main style="padding-top: 3.75rem" class="flex-grow main-container flex">
            <router-view :key="$route.name" />
        </main>

        <leaderboard-dialog />
        <daily-challenge-dialog />
        <challenge-friend-dialog />
        <game-statistics-dialog />
        <donation-dialog />
        <pause-overlay />
        <notification />
        <customize-dialog />
        <games-nav />

        <footer v-if="showFooter" class="p-2 mt-8 text-center">
            <span
                >&copy; {{ new Date().getFullYear() }}
                <span class="text-sm text-gray-500 inline-block ml-3">rev {{ version }}</span></span
            >
        </footer>
    </div>
</template>

<script lang="ts">
/* eslint-disable */
import { defineComponent, onBeforeUnmount, onMounted, computed } from 'vue';
import { appQuery } from '@/state/app.query';
import { appService } from '@/state/app.service';
import { tracking } from '@/core/tracking';
import { coreLocalStorage } from '@/core/core-local-storage';
import LeaderboardDialog from '@/views/LeaderBoardDialog.vue';
import DailyChallengeDialog from '@/views/DailyChallengeDialog.vue';
import ChallengeFriendDialog from '@/views/ChallengeFriendDialog.vue';
import PauseOverlay from '@/views/PauseOverlay.vue';
import NavBar from '@/components/NavBar.vue';
import SideBar from '@/components/SideBar.vue';
import Overlay from '@/components/Overlay.vue';
import GameStatisticsDialog from '@/views/GameStatisticsDialog.vue';
import Notification from '@/components/Notification.vue';
import DonationDialog from '@/views/DonationDialog.vue';
import GamesNav from '@/components/GamesNav.vue';
import { subscribeTo, useObservable } from '@/core/rxjs-helpers';
import { controllers } from '@/controllers/controllers';
import CustomizeDialog from '@/views/CustomizeDialog.vue';
import { customize } from '@/core/customize';

export default defineComponent({
    components: {
        LeaderboardDialog,
        DailyChallengeDialog,
        ChallengeFriendDialog,
        PauseOverlay,
        NavBar,
        SideBar,
        Overlay,
        GameStatisticsDialog,
        Notification,
        GamesNav,
        DonationDialog,
        CustomizeDialog,
    },

    setup() {
        const showFooter = useObservable(appQuery.showFooter$);
        const customizeOptions = useObservable(appQuery.customizeOptions$);
        let disablePageScroll = false;
        const version = process.env.VERSION;

        // load user customize
        customize.loadOptions();

        subscribeTo(appQuery.disablePageScroll$, (disabled) => {
            disablePageScroll = disabled;
            window.document.body.style.overflow = disabled ? 'hidden' : 'visible';
        });

        const registerScrollEvents = () => {
            const el = document.querySelector('.main-container');
            if (el) {
                el.addEventListener('touchmove', (e) => {
                    if (disablePageScroll) {
                        e.preventDefault();
                    }
                });
            }
        };

        const createFingerprint = () => {
            // todo: move this to games to reduce file size
            // create finger print a head of time
            setTimeout(() => {
                coreLocalStorage.getOrCreateFingerprint();
            }, 1000);
        };

        const registerVisibilityEvents = () => {
            // https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API
            // Set the name of the hidden property and the change event for visibility
            let hidden = 'hidden';
            let visibilityChange = 'visibilitychange';
            if (typeof document.hidden !== 'undefined') {
                // Opera 12.10 and Firefox 18 and later support
                hidden = 'hidden';
                visibilityChange = 'visibilitychange';
                // eslint-disable-next-line
            } else if (typeof (document as any).msHidden !== 'undefined') {
                hidden = 'msHidden';
                visibilityChange = 'msvisibilitychange';
                // eslint-disable-next-line
            } else if (typeof (document as any).webkitHidden !== 'undefined') {
                hidden = 'webkitHidden';
                visibilityChange = 'webkitvisibilitychange';
            }
            document.addEventListener(visibilityChange, () => {
                // eslint-disable-next-line
                if ((document as any)[hidden]) {
                    appService.setAppHiddenStatus(true);
                } else {
                    appService.setAppHiddenStatus(false);
                }
            });

            // https://developer.mozilla.org/en-US/docs/Web/API/Window/blur_event
            window.addEventListener('blur', () => {
                appService.setAppHiddenStatus(true);
            });
            window.addEventListener('focus', () => {
                appService.setAppHiddenStatus(false);
            });

            // https://www.billerickson.net/code/track-pwa-install-in-google-analytics/
            // https://web.dev/customize-install/
            window.addEventListener('appinstalled', () => {
                try {
                    console.log('---: appinstalled');
                    tracking.event('app-installed', {});
                } catch (e) {
                    console.error(e);
                }
            });

            window.addEventListener('DOMContentLoaded', () => {
                let displayMode = 'browser';
                // eslint-disable-next-line
                if ((navigator as any).standalone) {
                    displayMode = 'standalone-ios';
                }
                if (window.matchMedia('(display-mode: standalone)').matches) {
                    displayMode = 'standalone';
                }
                // Log launch display mode to analytics
                if (displayMode != 'browser') {
                    try {
                        tracking.event('app-launch', {
                            eventLabel: displayMode,
                        });
                    } catch (e) {
                        console.error(e);
                    }
                }
                console.log('DISPLAY_MODE_LAUNCH:', displayMode);
            });
        };

        const bgStyle = computed(() =>{
            if (customizeOptions.value) {
                const bg = customize.getBackgroundByName(customizeOptions.value.background);
                let finalBg = bg.gradient;
                const pattern = customize.getPattern(customizeOptions.value.pattern);
                if (pattern) {
                    const color = bg.patternColor ? bg.patternColor : '#aaaaaa';
                    const svg = pattern.svg.replace('[color]', color.replace('#', '%23'))
                    finalBg = `url("${svg}"), ${bg.gradient}`;
                }
                return {
                    background: bg.solid,
                    // @ts-ignore
                    background: finalBg,
                };
            }
            return {};
        });

        onMounted(() => {
            registerScrollEvents();
            registerVisibilityEvents();
            createFingerprint();
            controllers.init();
        });

        onBeforeUnmount(() => {
            controllers.destroy();
        });

        return {
            showFooter,
            bgStyle,
            customizeOptions,
            version
        };
    },
});
</script>

<style>
html,
body {
    background-color: #121212;
}
.noselect {
    -webkit-tap-highlight-color: rgba(255, 255, 255, 0);
    -webkit-touch-callout: none; /* iOS Safari */
    -webkit-user-select: none; /* Safari */
    -khtml-user-select: none; /* Konqueror HTML */
    -moz-user-select: none; /* Old versions of Firefox */
    -ms-user-select: none; /* Internet Explorer/Edge */
    user-select: none; /* Non-prefixed version, currently
                                  supported by Chrome, Edge, Opera and Firefox */
}

button,
a {
    -webkit-tap-highlight-color: rgba(255, 255, 255, 0);
}

.application {
    min-height: 100vh;
    color: #fff;

    /*
    background: rgb(47, 70, 185);
    background: radial-gradient(circle, rgb(111, 133, 248) 0%, rgba(47, 70, 185, 1) 85%);
    */

    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='192' height='192' viewBox='0 0 192 192'%3E%3Cpath fill='%239C92AC' fill-opacity='0.4' d='M192 15v2a11 11 0 0 0-11 11c0 1.94 1.16 4.75 2.53 6.11l2.36 2.36a6.93 6.93 0 0 1 1.22 7.56l-.43.84a8.08 8.08 0 0 1-6.66 4.13H145v35.02a6.1 6.1 0 0 0 3.03 4.87l.84.43c1.58.79 4 .4 5.24-.85l2.36-2.36a12.04 12.04 0 0 1 7.51-3.11 13 13 0 1 1 .02 26 12 12 0 0 1-7.53-3.11l-2.36-2.36a4.93 4.93 0 0 0-5.24-.85l-.84.43a6.1 6.1 0 0 0-3.03 4.87V143h35.02a8.08 8.08 0 0 1 6.66 4.13l.43.84a6.91 6.91 0 0 1-1.22 7.56l-2.36 2.36A10.06 10.06 0 0 0 181 164a11 11 0 0 0 11 11v2a13 13 0 0 1-13-13 12 12 0 0 1 3.11-7.53l2.36-2.36a4.93 4.93 0 0 0 .85-5.24l-.43-.84a6.1 6.1 0 0 0-4.87-3.03H145v35.02a8.08 8.08 0 0 1-4.13 6.66l-.84.43a6.91 6.91 0 0 1-7.56-1.22l-2.36-2.36A10.06 10.06 0 0 0 124 181a11 11 0 0 0-11 11h-2a13 13 0 0 1 13-13c2.47 0 5.79 1.37 7.53 3.11l2.36 2.36a4.94 4.94 0 0 0 5.24.85l.84-.43a6.1 6.1 0 0 0 3.03-4.87V145h-35.02a8.08 8.08 0 0 1-6.66-4.13l-.43-.84a6.91 6.91 0 0 1 1.22-7.56l2.36-2.36A10.06 10.06 0 0 0 107 124a11 11 0 0 0-22 0c0 1.94 1.16 4.75 2.53 6.11l2.36 2.36a6.93 6.93 0 0 1 1.22 7.56l-.43.84a8.08 8.08 0 0 1-6.66 4.13H49v35.02a6.1 6.1 0 0 0 3.03 4.87l.84.43c1.58.79 4 .4 5.24-.85l2.36-2.36a12.04 12.04 0 0 1 7.51-3.11A13 13 0 0 1 81 192h-2a11 11 0 0 0-11-11c-1.94 0-4.75 1.16-6.11 2.53l-2.36 2.36a6.93 6.93 0 0 1-7.56 1.22l-.84-.43a8.08 8.08 0 0 1-4.13-6.66V145H11.98a6.1 6.1 0 0 0-4.87 3.03l-.43.84c-.79 1.58-.4 4 .85 5.24l2.36 2.36a12.04 12.04 0 0 1 3.11 7.51A13 13 0 0 1 0 177v-2a11 11 0 0 0 11-11c0-1.94-1.16-4.75-2.53-6.11l-2.36-2.36a6.93 6.93 0 0 1-1.22-7.56l.43-.84a8.08 8.08 0 0 1 6.66-4.13H47v-35.02a6.1 6.1 0 0 0-3.03-4.87l-.84-.43c-1.59-.8-4-.4-5.24.85l-2.36 2.36A12 12 0 0 1 28 109a13 13 0 1 1 0-26c2.47 0 5.79 1.37 7.53 3.11l2.36 2.36a4.94 4.94 0 0 0 5.24.85l.84-.43A6.1 6.1 0 0 0 47 84.02V49H11.98a8.08 8.08 0 0 1-6.66-4.13l-.43-.84a6.91 6.91 0 0 1 1.22-7.56l2.36-2.36A10.06 10.06 0 0 0 11 28 11 11 0 0 0 0 17v-2a13 13 0 0 1 13 13c0 2.47-1.37 5.79-3.11 7.53l-2.36 2.36a4.94 4.94 0 0 0-.85 5.24l.43.84A6.1 6.1 0 0 0 11.98 47H47V11.98a8.08 8.08 0 0 1 4.13-6.66l.84-.43a6.91 6.91 0 0 1 7.56 1.22l2.36 2.36A10.06 10.06 0 0 0 68 11 11 11 0 0 0 79 0h2a13 13 0 0 1-13 13 12 12 0 0 1-7.53-3.11l-2.36-2.36a4.93 4.93 0 0 0-5.24-.85l-.84.43A6.1 6.1 0 0 0 49 11.98V47h35.02a8.08 8.08 0 0 1 6.66 4.13l.43.84a6.91 6.91 0 0 1-1.22 7.56l-2.36 2.36A10.06 10.06 0 0 0 85 68a11 11 0 0 0 22 0c0-1.94-1.16-4.75-2.53-6.11l-2.36-2.36a6.93 6.93 0 0 1-1.22-7.56l.43-.84a8.08 8.08 0 0 1 6.66-4.13H143V11.98a6.1 6.1 0 0 0-3.03-4.87l-.84-.43c-1.59-.8-4-.4-5.24.85l-2.36 2.36A12 12 0 0 1 124 13a13 13 0 0 1-13-13h2a11 11 0 0 0 11 11c1.94 0 4.75-1.16 6.11-2.53l2.36-2.36a6.93 6.93 0 0 1 7.56-1.22l.84.43a8.08 8.08 0 0 1 4.13 6.66V47h35.02a6.1 6.1 0 0 0 4.87-3.03l.43-.84c.8-1.59.4-4-.85-5.24l-2.36-2.36A12 12 0 0 1 179 28a13 13 0 0 1 13-13zM84.02 143a6.1 6.1 0 0 0 4.87-3.03l.43-.84c.8-1.59.4-4-.85-5.24l-2.36-2.36A12 12 0 0 1 83 124a13 13 0 1 1 26 0c0 2.47-1.37 5.79-3.11 7.53l-2.36 2.36a4.94 4.94 0 0 0-.85 5.24l.43.84a6.1 6.1 0 0 0 4.87 3.03H143v-35.02a8.08 8.08 0 0 1 4.13-6.66l.84-.43a6.91 6.91 0 0 1 7.56 1.22l2.36 2.36A10.06 10.06 0 0 0 164 107a11 11 0 0 0 0-22c-1.94 0-4.75 1.16-6.11 2.53l-2.36 2.36a6.93 6.93 0 0 1-7.56 1.22l-.84-.43a8.08 8.08 0 0 1-4.13-6.66V49h-35.02a6.1 6.1 0 0 0-4.87 3.03l-.43.84c-.79 1.58-.4 4 .85 5.24l2.36 2.36a12.04 12.04 0 0 1 3.11 7.51A13 13 0 1 1 83 68a12 12 0 0 1 3.11-7.53l2.36-2.36a4.93 4.93 0 0 0 .85-5.24l-.43-.84A6.1 6.1 0 0 0 84.02 49H49v35.02a8.08 8.08 0 0 1-4.13 6.66l-.84.43a6.91 6.91 0 0 1-7.56-1.22l-2.36-2.36A10.06 10.06 0 0 0 28 85a11 11 0 0 0 0 22c1.94 0 4.75-1.16 6.11-2.53l2.36-2.36a6.93 6.93 0 0 1 7.56-1.22l.84.43a8.08 8.08 0 0 1 4.13 6.66V143h35.02z'%3E%3C/path%3E%3C/svg%3E"), radial-gradient(circle, rgb(111, 133, 248) 0%, rgba(47, 70, 185, 1) 85%);
    /*
background: rgb(4, 86, 4);
background: radial-gradient(circle, rgb(2, 136, 2) 0%, rgb(4, 86, 4) 85%);
*/
}

.anim-scale-enter-active,
.anim-scale-leave-active {
    transition: transform 0.3s ease;
}

.anim-scale-enter-from,
.anim-scale-leave-to {
    transform: scale(0.5, 0.5);
    opacity: 0;
    transition: all 150ms ease-in 0s;
}

.anim-fade-enter-active,
.anim-fade-leave-active {
    transition: opacity 150ms ease-in 0s;
}

.anim-fade-enter-from,
.anim-fade-leave-to {
    opacity: 0;
}

.anim-slide-left-enter-active,
.anim-slide-left-leave-active {
    transition: transform 0.2s ease;
}

.anim-slide-left-enter-from,
.anim-slide-left-leave-to {
    transform: translateX(-100%);
    transition: all 150ms ease-in 0s;
}

.anim-slide-bottom-enter-active,
.anim-slide-bottom-leave-active {
    transition: transform 0.2s ease;
}

.anim-slide-bottom-enter-from,
.anim-slide-bottom-leave-to {
    transform: translateY(100%);
    transition: all 150ms ease-in 0s;
}

.anim-slide-top-enter-active,
.anim-slide-top-leave-active {
    transition: transform 0.2s ease;
}

.anim-slide-top-enter-from,
.anim-slide-top-leave-to {
    transform: translateY(-100%);
    transition: all 150ms ease-in 0s;
}

footer {
    background-color: rgba(35, 35, 35, 0.8);
}

/* ---- intro button ---- */
.s-button.intro {
    width: 100%;
    display: inline-block;
    @apply max-w-xs;
    @apply shadow-lg;
    @apply px-5;
    @apply focus:outline-none;
    @apply border;
    @apply py-3;
    @apply text-xl;
    @apply rounded-md;
}

/* ---- blue button ---- */
.s-button.blue {
    @apply bg-blue-600;
    @apply border-blue-400;
}
.s-button.blue:hover {
    @apply bg-blue-500;
}
.s-button.blue.outlined {
    background-color: inherit;
    @apply border-blue-500;
    @apply text-blue-500;
    border-width: 1px;
    border-style: solid;
}
.s-button.blue.outlined:hover {
    background-color: #4299e133;
}

/* ---- orange button ---- */
.s-button.orange {
    @apply bg-yellow-600;
    @apply border-yellow-400;
}
.s-button.orange:hover {
    @apply bg-yellow-500;
}
.s-button.orange.outlined {
    background-color: inherit;
    @apply border-yellow-500;
    @apply text-yellow-500;
    border-width: 1px;
    border-style: solid;
}
.s-button.orange.outlined:hover {
    background-color: #ed893633;
}

/* ---- green button ---- */
.s-button.green {
    @apply bg-green-700;
    @apply border-green-600;
    @apply hover:bg-green-600;
}

/* ---- white button ---- */
.s-button.white.outlined {
    background-color: inherit;
    @apply border-white;
    border-width: 1px;
    border-style: solid;
}
.s-button.white.outlined:hover {
    background-color: #ffffff33;
}

/* --- containers --- */
.s-container-1 {
    background-color: rgba(70, 70, 70, 0.68);
    margin-left: 5px;
    margin-right: 5px;
    @apply p-5;
    @apply pb-10;
}

.s-container-2 {
    background-color: rgba(70, 70, 70, 0.8);
    margin-left: 5px;
    margin-right: 5px;
    @apply p-5;
    @apply pb-10;
}

</style>
